import { memo } from 'react';
import { Checkbox, Chip, ListItemButton, Stack, Tooltip } from '@mui/material';

import { CasChip } from 'src/compass/shared/components';
import Iconify from 'src/components/iconify';

// Direct paths, not the application barrel: this file is inside it, and importing it from itself
// is a cycle (see CasPendingEmailsPill in CasContextProviders).
import { SendChannelType } from '../composeMode';
import { IBatchRow } from './batchRow';

/*
  Every prop is a primitive or stable, and the component is memoized, because a batch routinely
  lists hundreds of rows: without this, every keystroke and every checkbox re-rendered all of them
  (~620ms per click at 257 rows). What a row overrides and which channel it resolved to are passed
  as strings for the same reason - the dialog rebuilds those objects on each render.
*/
interface Props {
  row: IBatchRow;
  selected: boolean;
  included: boolean;
  // What this row overrides, already labelled: "To/CC", "Msg", "To/CC, Msg", or empty.
  customLabel: string;
  // Resolved by the dialog, which owns the per-row overrides this depends on. One or the other.
  channel?: SendChannelType;
  reason?: string;
  onSelect: (key: string) => void;
  onToggleInclude: (key: string) => void;
}

function BatchRowListItem({
  row,
  selected,
  included,
  customLabel,
  channel,
  reason,
  onSelect,
  onToggleInclude,
}: Props) {
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(row.key)}
      /*
        Deliberately still selectable and still checkable when unresolved. Disabling it - which is
        what this did while every message was an email - leaves the sender no way to fix the row
        from here, and quietly drops a row they asked to write to.
      */
      sx={{ py: 0.5, px: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%', minWidth: 0 }}>
        <Checkbox
          size="small"
          checked={included}
          // The row itself selects; the checkbox only includes/excludes.
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleInclude(row.key)}
          sx={{ p: 0.5 }}
        />
        <div
          style={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.label}
        </div>
        {/* Named for what was changed rather than a generic "Custom": which override a row carries
            decides whether the surprise is who gets the message or what it says.  One chip listing
            both rather than two chips - the row is narrow, and two would read as two separate
            things to undo when Revert clears them together. */}
        {/* An excluded row says nothing about channel, overrides or problems: none of it applies to
            a message that is not going out. The row stays selectable so it can be looked at and
            put back. */}
        {included && customLabel && <CasChip label={customLabel} size="small" />}
        {/* Which way this one goes. Only worth saying when it is settled - an unresolved row shows
            the warning instead, which is the more useful thing to read. */}
        {included && channel && (
          <Chip
            // MUI's Chip rather than CasChip: this wants the same soft, channel-coloured pill the
            // compose window puts against a contact (see channelTag), and CasChip only offers its
            // own warning/disabled pair. A filled default Chip is near-black under this theme.
            label={channel === 'sms' ? 'SMS' : 'Email'}
            size="small"
            variant="soft"
            color={channel === 'sms' ? 'info' : 'success'}
            sx={{ textTransform: 'uppercase', fontSize: 10 }}
          />
        )}
        {included && !!reason && (
          <Tooltip title={reason}>
            <span style={{ display: 'flex' }}>
              <Iconify icon="eva:alert-triangle-fill" width="16px" height="16px" color="#B71D18" />
            </span>
          </Tooltip>
        )}
      </Stack>
    </ListItemButton>
  );
}

export default memo(BatchRowListItem);
